<template>
    <form v-on:submit.prevent="handleSubmit" :class="'form' + (theme !== '' ? ' form--' + theme : '')" novalidate>
        <div class="form-fieldset">
            <div class="form-group" :class="{ 'has-error': submitted && $v.form.name.$error, 'has-success': submitted && !$v.form.name.$error }">
                <label class="u-sr-only" for="register-name">Naam</label>
                <input type="text" v-model="form.name" id="register-name" name="naam" class="form-control" placeholder="Vul hier je voor- en achternaam in" />
                <div v-if="submitted && $v.form.name.$error" class="form-validation">
                    <span v-if="!$v.form.name.required">Naam is verplicht</span>
                    <span v-if="!$v.form.name.minLength">Naam moet minstens 2 karakters lang zijn</span>
                </div>
            </div>

            <div class="form-group" :class="{ 'has-error': submitted && $v.form.email.$error, 'has-success': submitted && !$v.form.email.$error }">
                <label class="u-sr-only" for="login-email">E-mailadres</label>
                <input type="email" v-model="form.email" id="login-email" name="emailadres" class="form-control" placeholder="Vul hier je e-mailadres in" />
                <div v-if="submitted && $v.form.email.$error" class="form-validation">
                    <span v-if="!$v.form.email.required">E-mailadres is verplicht</span>
                    <span v-if="!$v.form.email.email">E-mailadres is ongeldig</span>
                </div>
            </div>

            <div class="form-group" :class="{ 'has-error': $v.form.password.$error, 'has-success': submitted && !$v.form.password.$error }" @keyup="$v.form.$touch()">
                <label class="u-sr-only" for="register-password">Wachtwoord</label>
                <input type="password" v-model="form.password" id="register-password" name="wachtwoord" class="form-control" placeholder="Vul hier je wachtwoord in" />
                <div v-if="$v.form.password.$error" class="form-validation">
                    <span v-if="!$v.form.password.required">Wachtwoord is verplicht</span>
                    <span v-if="!$v.form.password.minLength">Wachtwoord moet minstens 8 karakters lang zijn</span>
                    <span v-if="!$v.form.password.passwordValidatorUpper">Wachtwoord moet minstens 1 hoofdletter bevatten</span>
                    <span v-if="!$v.form.password.passwordValidatorInteger">Wachtwoord moet minstens 1 cijfer bevatten</span>
                    <span v-if="!$v.form.password.passwordValidatorSpecial">Wachtwoord moet minstens 1 speciaal teken bevatten</span>
                </div>
            </div>

            <div class="form-group" :class="{ 'has-error': $v.form.passwordRepeat.$error, 'has-success': submitted && !$v.form.passwordRepeat.$error }">
                <label class="u-sr-only" for="register-password-repeat">Herhaal wachtwoord</label>
                <input type="password" v-model="form.passwordRepeat" id="register-password-repeat" name="password-repeat" class="form-control" placeholder="Herhaal je wachtwoord" />
                <div v-if="$v.form.passwordRepeat.$error" class="form-validation">
                    <span v-if="!$v.form.passwordRepeat.required">Herhaal je wachtwoord</span>
                    <span v-else-if="!$v.form.passwordRepeat.sameAsPassword">Wachtwoorden moeten overeen komen</span>
                </div>
            </div>

            <div class="custom-checkbox" :class="{ 'has-error': submitted && $v.form.privacy.$error }">
                <label for="register-privacy" class="custom-control-label">
                    <input type="checkbox" v-model="form.privacy" name="privacy" id="register-privacy" class="custom-control-input" />
                    <span class="custom-control-name">Ik ga akkoord met de <a class="u-font-weight-bold" href="https://www.addsoft.nl/voorwaarden" target="_blank">algemene voorwaarden</a></span>
                </label>
                <div v-if="submitted && $v.form.privacy.$error" class="form-validation">
                    <span v-if="!$v.form.privacy.checked">Je dient akkoord te gaan met de algemene voorwaarden</span>
                </div>
            </div>
        </div>

        <div class="g-cols:1/auto>sm || g-gap:1-xs || u-text-left || align-items-center">
            <p class="form-text">Heb je al een account? <router-link class="form-link" to="/">Klik hier om in te loggen</router-link></p>

            <button type="submit" class="[ c-btn c-btn--info ] form-submit-btn" :disabled="status.registering">
                <span v-show="status.registering"><spinner/></span>
                <span v-show="!status.registering">Registreren</span>
                <svg v-show="!status.registering" xmlns="http://www.w3.org/2000/svg" width="15.589" height="12.389" viewBox="0 0 15.589 12.389">
                    <g fill="none" stroke="#fff" stroke-linecap="round" stroke-width="2">
                        <path d="M1 6.166h12.875"/>
                        <path d="M8.15 1.4l6.438 4.794-6.438 4.8" stroke-linejoin="round"/>
                        <path d="M8.15 1.399l6.438 4.795-6.437 4.8" stroke-linejoin="round"/>
                    </g>
                </svg>
            </button>
        </div>
    </form>
</template>

<script>
    import { mapState } from 'vuex';
    import {
        required,
        email,
        minLength,
        sameAs,
    } from 'vuelidate/lib/validators';
    import normalizeFormData from '../../helpers/normalizeFormData';
    import { accountService } from '../../services';
    import Spinner from '../Spinner.vue';

    export default {
        name: 'RegisterForm',
        components: { Spinner },
        data () {
            return {
                submitted: false,
                form: {
                    name: '',
                    password: '',
                    passwordRepeat: '',
                    email: '',
                    privacy: '',
                },
            };
        },
        props: {
            theme: {
                type: String,
                default: '',
            },
        },
        validations: {
            form: {
                name: { required, minLength: minLength(2) },
                password: {
                    required,
                    minLength: minLength(8),
                    // custom validator
                    passwordValidatorUpper: (password) => /[A-Z]/.test(password),
                    passwordValidatorInteger: (password) => /[0-9]/.test(password),
                    passwordValidatorSpecial: (password) => /[!"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~]/.test(password),
                },
                passwordRepeat: { required, sameAsPassword: sameAs('password') },
                email: { required, email },
                privacy: {
                    checked: (value) => value === true,
                },
            },
        },
        computed: {
            ...mapState('account', ['status']),
        },
        methods: {
            handleSubmit (e) {
                this.submitted = true;

                // validate the form
                this.$v.form.$touch();

                // if its still pending or an error is returned do not submit
                if (this.$v.form.$pending || this.$v.form.$error) return;

                // normalize form data
                const data = normalizeFormData(e.target);

                this.$store.commit('account/registerRequest');

                // register new account
                accountService
                    .register(data)
                    .then(() => {
                        this.$store.commit('account/registerSuccess');

                        // reset errors
                        this.$store.dispatch('alert/clear');

                        // show success message
                        this.$emit('formIsSent', true);
                        /*this.$store.dispatch('alert/success', { message: 'Er is een activatie-mail aan je verzonden.' }, { root: true }).then(() => {
                            // emit event to parent
                            this.$emit('formIsSent', true);
                        });*/
                    })
                    .catch((error) => {
                        this.$store.commit('account/registerFailure');
                        this.$store.dispatch('alert/error', { error }, { root: true });

                        // emit event to parent
                        this.$emit('formIsSent', false);
                    });
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import "../../assets/scss/_essentials.scss";

    .form-text {
        color: $brand-primary;
        font-size: rem(15);
        margin-bottom: 0;
        margin-left: grid(1, sm);
    }

    .form-link {
        text-decoration: underline;
    }

    .form-submit-btn {
        @include media-breakpoint-down(xsl) {
            justify-self: center;
        }
    }
</style>
